/**
 * Plugin System Architecture
 * VST-style plugin system groundwork for custom effects
 */

/**
 * Plugin Types
 */
export const PLUGIN_TYPES = {
  EFFECT: 'effect',           // Audio effect processor
  INSTRUMENT: 'instrument',   // Virtual instrument/synth
  ANALYZER: 'analyzer',       // Metering/visualization
  MIDI: 'midi',              // MIDI processor
};

/**
 * Plugin Categories
 */
export const PLUGIN_CATEGORIES = {
  DYNAMICS: 'dynamics',       // Compressors, limiters, gates
  EQ: 'eq',                   // Equalizers
  MODULATION: 'modulation',   // Chorus, flanger, phaser
  DELAY: 'delay',             // Delays, echoes
  REVERB: 'reverb',           // Reverbs
  DISTORTION: 'distortion',   // Saturation, overdrive
  FILTER: 'filter',           // Filters
  UTILITY: 'utility',         // Gain, pan, phase
  SYNTH: 'synth',             // Synthesizers
  SAMPLER: 'sampler',         // Sample players
  METER: 'meter',             // Analyzers, meters
};

/**
 * Parameter Types for plugin parameters
 */
export const PARAM_TYPES = {
  FLOAT: 'float',     // Continuous value
  INT: 'int',         // Integer value
  BOOL: 'bool',       // Boolean on/off
  ENUM: 'enum',       // Enumerated choices
  STRING: 'string',   // Text value
};

/**
 * Plugin Parameter Definition
 */
export class PluginParameter {
  constructor({
    id,
    name,
    type = PARAM_TYPES.FLOAT,
    defaultValue,
    minValue,
    maxValue,
    step = null,
    unit = '',
    choices = null,  // For enum type
    displayValue = null, // Custom display function
  }) {
    this.id = id;
    this.name = name;
    this.type = type;
    this.defaultValue = defaultValue;
    this.minValue = minValue;
    this.maxValue = maxValue;
    this.step = step;
    this.unit = unit;
    this.choices = choices;
    this.displayValue = displayValue;
    this.value = defaultValue;
    this.listeners = [];
  }
  
  setValue(value) {
    // Clamp value for numeric types
    if (this.type === PARAM_TYPES.FLOAT || this.type === PARAM_TYPES.INT) {
      value = Math.max(this.minValue, Math.min(this.maxValue, value));
      if (this.type === PARAM_TYPES.INT) {
        value = Math.round(value);
      }
    }
    
    const oldValue = this.value;
    this.value = value;
    
    // Notify listeners
    this.listeners.forEach(listener => listener(value, oldValue, this));
  }
  
  getValue() {
    return this.value;
  }
  
  getDisplayValue() {
    if (this.displayValue) {
      return this.displayValue(this.value);
    }
    if (this.type === PARAM_TYPES.ENUM && this.choices) {
      return this.choices[this.value] || this.value;
    }
    return `${this.value}${this.unit}`;
  }
  
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }
  
  reset() {
    this.setValue(this.defaultValue);
  }
  
  toJSON() {
    return {
      id: this.id,
      value: this.value,
    };
  }
  
  fromJSON(data) {
    if (data.value !== undefined) {
      this.setValue(data.value);
    }
  }
}

/**
 * Base Plugin Class
 * All plugins extend this class
 */
export class Plugin {
  constructor(audioContext, options = {}) {
    this.audioContext = audioContext;
    this.id = options.id || `plugin-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.name = options.name || 'Plugin';
    this.type = options.type || PLUGIN_TYPES.EFFECT;
    this.category = options.category || PLUGIN_CATEGORIES.UTILITY;
    this.version = options.version || '1.0.0';
    this.author = options.author || 'Unknown';
    this.description = options.description || '';
    
    this.enabled = true;
    this.parameters = new Map();
    this.presets = [];
    this.currentPreset = null;
    
    // Audio nodes
    this.inputNode = null;
    this.outputNode = null;
    this.bypassNode = null;
    
    // Initialize
    this._initializeNodes();
    this._initializeParameters();
  }
  
  /**
   * Override in subclass to create audio nodes
   */
  _initializeNodes() {
    // Default: passthrough
    this.inputNode = this.audioContext.createGain();
    this.outputNode = this.audioContext.createGain();
    this.bypassNode = this.audioContext.createGain();
    
    // Connect bypass path
    this.inputNode.connect(this.bypassNode);
    this.bypassNode.connect(this.outputNode);
    
    this.bypassNode.gain.value = 0; // Start with bypass off
  }
  
  /**
   * Override in subclass to define parameters
   */
  _initializeParameters() {
    // Example parameter
    this.addParameter(new PluginParameter({
      id: 'mix',
      name: 'Mix',
      type: PARAM_TYPES.FLOAT,
      defaultValue: 1.0,
      minValue: 0,
      maxValue: 1,
      unit: '%',
      displayValue: (v) => `${Math.round(v * 100)}%`,
    }));
  }
  
  /**
   * Add a parameter to the plugin
   */
  addParameter(param) {
    this.parameters.set(param.id, param);
    
    // Listen for changes
    param.addListener((value) => {
      this.onParameterChange(param.id, value);
    });
    
    return param;
  }
  
  /**
   * Get a parameter by ID
   */
  getParameter(id) {
    return this.parameters.get(id);
  }
  
  /**
   * Set a parameter value
   */
  setParameter(id, value) {
    const param = this.parameters.get(id);
    if (param) {
      param.setValue(value);
    }
  }
  
  /**
   * Override in subclass to handle parameter changes
   */
  onParameterChange(paramId, value) {
    // Subclass implementation
  }
  
  /**
   * Connect plugin input
   */
  connectInput(sourceNode) {
    sourceNode.connect(this.inputNode);
  }
  
  /**
   * Connect plugin output
   */
  connectOutput(destinationNode) {
    this.outputNode.connect(destinationNode);
  }
  
  /**
   * Disconnect all connections
   */
  disconnect() {
    this.inputNode?.disconnect();
    this.outputNode?.disconnect();
    this.bypassNode?.disconnect();
  }
  
  /**
   * Enable/disable the plugin
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    this._updateBypass();
  }
  
  /**
   * Toggle bypass
   */
  toggleBypass() {
    this.setEnabled(!this.enabled);
  }
  
  /**
   * Update bypass routing
   */
  _updateBypass() {
    if (this.bypassNode) {
      this.bypassNode.gain.value = this.enabled ? 0 : 1;
    }
    // Update processing path gain in subclass
  }
  
  /**
   * Save current state as preset
   */
  savePreset(name) {
    const preset = {
      id: `preset-${Date.now()}`,
      name,
      parameters: {},
    };
    
    this.parameters.forEach((param, id) => {
      preset.parameters[id] = param.getValue();
    });
    
    this.presets.push(preset);
    return preset;
  }
  
  /**
   * Load a preset
   */
  loadPreset(presetId) {
    const preset = this.presets.find(p => p.id === presetId);
    if (!preset) return false;
    
    Object.entries(preset.parameters).forEach(([id, value]) => {
      this.setParameter(id, value);
    });
    
    this.currentPreset = presetId;
    return true;
  }
  
  /**
   * Export plugin state to JSON
   */
  toJSON() {
    const params = {};
    this.parameters.forEach((param, id) => {
      params[id] = param.getValue();
    });
    
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      category: this.category,
      enabled: this.enabled,
      parameters: params,
      currentPreset: this.currentPreset,
    };
  }
  
  /**
   * Import plugin state from JSON
   */
  fromJSON(data) {
    if (data.enabled !== undefined) {
      this.setEnabled(data.enabled);
    }
    
    if (data.parameters) {
      Object.entries(data.parameters).forEach(([id, value]) => {
        this.setParameter(id, value);
      });
    }
    
    if (data.currentPreset) {
      this.loadPreset(data.currentPreset);
    }
  }
  
  /**
   * Get plugin metadata
   */
  getMetadata() {
    return {
      id: this.id,
      name: this.name,
      type: this.type,
      category: this.category,
      version: this.version,
      author: this.author,
      description: this.description,
      parameterCount: this.parameters.size,
      presetCount: this.presets.length,
    };
  }
}

/**
 * Plugin Registry
 * Manages available plugins and instances
 */
export class PluginRegistry {
  constructor() {
    this.registeredPlugins = new Map(); // name -> Plugin class
    this.instances = new Map();          // instanceId -> Plugin instance
    this.listeners = [];
  }
  
  /**
   * Register a plugin class
   */
  registerPlugin(name, PluginClass, metadata = {}) {
    this.registeredPlugins.set(name, {
      PluginClass,
      metadata: {
        name,
        ...metadata,
      },
    });
    
    this._notifyListeners('register', { name, metadata });
  }
  
  /**
   * Unregister a plugin
   */
  unregisterPlugin(name) {
    this.registeredPlugins.delete(name);
    this._notifyListeners('unregister', { name });
  }
  
  /**
   * Create a plugin instance
   */
  createInstance(pluginName, audioContext, options = {}) {
    const registered = this.registeredPlugins.get(pluginName);
    if (!registered) {
      throw new Error(`Plugin "${pluginName}" not registered`);
    }
    
    const instance = new registered.PluginClass(audioContext, options);
    this.instances.set(instance.id, instance);
    
    this._notifyListeners('create', { pluginName, instance });
    
    return instance;
  }
  
  /**
   * Destroy a plugin instance
   */
  destroyInstance(instanceId) {
    const instance = this.instances.get(instanceId);
    if (instance) {
      instance.disconnect();
      this.instances.delete(instanceId);
      this._notifyListeners('destroy', { instanceId });
    }
  }
  
  /**
   * Get all registered plugins
   */
  getRegisteredPlugins() {
    const plugins = [];
    this.registeredPlugins.forEach((value, name) => {
      plugins.push({
        name,
        ...value.metadata,
      });
    });
    return plugins;
  }
  
  /**
   * Get plugins by type
   */
  getPluginsByType(type) {
    return this.getRegisteredPlugins().filter(p => p.type === type);
  }
  
  /**
   * Get plugins by category
   */
  getPluginsByCategory(category) {
    return this.getRegisteredPlugins().filter(p => p.category === category);
  }
  
  /**
   * Get instance by ID
   */
  getInstance(instanceId) {
    return this.instances.get(instanceId);
  }
  
  /**
   * Add event listener
   */
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }
  
  _notifyListeners(event, data) {
    this.listeners.forEach(listener => listener(event, data));
  }
}

/**
 * Plugin Chain
 * Manages an ordered chain of plugins
 */
export class PluginChain {
  constructor(audioContext) {
    this.audioContext = audioContext;
    this.plugins = [];
    this.input = audioContext.createGain();
    this.output = audioContext.createGain();
    
    // Default: direct connection
    this.input.connect(this.output);
  }
  
  /**
   * Add a plugin to the chain
   */
  addPlugin(plugin, index = -1) {
    if (index < 0 || index >= this.plugins.length) {
      this.plugins.push(plugin);
    } else {
      this.plugins.splice(index, 0, plugin);
    }
    
    this._rebuildChain();
    return plugin;
  }
  
  /**
   * Remove a plugin from the chain
   */
  removePlugin(pluginId) {
    const index = this.plugins.findIndex(p => p.id === pluginId);
    if (index >= 0) {
      const [removed] = this.plugins.splice(index, 1);
      removed.disconnect();
      this._rebuildChain();
      return removed;
    }
    return null;
  }
  
  /**
   * Move a plugin to a new position
   */
  movePlugin(pluginId, newIndex) {
    const currentIndex = this.plugins.findIndex(p => p.id === pluginId);
    if (currentIndex >= 0 && newIndex >= 0 && newIndex < this.plugins.length) {
      const [plugin] = this.plugins.splice(currentIndex, 1);
      this.plugins.splice(newIndex, 0, plugin);
      this._rebuildChain();
    }
  }
  
  /**
   * Rebuild the audio chain connections
   */
  _rebuildChain() {
    // Disconnect everything
    this.input.disconnect();
    this.plugins.forEach(p => p.disconnect());
    
    if (this.plugins.length === 0) {
      // Direct connection
      this.input.connect(this.output);
      return;
    }
    
    // Connect in sequence
    let previous = this.input;
    
    for (const plugin of this.plugins) {
      plugin.connectInput(previous);
      previous = plugin.outputNode;
    }
    
    previous.connect(this.output);
  }
  
  /**
   * Get all plugins in order
   */
  getPlugins() {
    return [...this.plugins];
  }
  
  /**
   * Export chain state
   */
  toJSON() {
    return {
      plugins: this.plugins.map(p => p.toJSON()),
    };
  }
  
  /**
   * Import chain state (requires plugin instances to already exist)
   */
  fromJSON(data) {
    if (data.plugins) {
      data.plugins.forEach((pluginData, index) => {
        if (this.plugins[index]) {
          this.plugins[index].fromJSON(pluginData);
        }
      });
    }
  }
}

// Create global registry instance
export const pluginRegistry = new PluginRegistry();

export default {
  Plugin,
  PluginParameter,
  PluginRegistry,
  PluginChain,
  pluginRegistry,
  PLUGIN_TYPES,
  PLUGIN_CATEGORIES,
  PARAM_TYPES,
};
